/**
 * When a distribution list runs, and what period each run covers (spec §9.5,
 * §13.6 digests). Pure, and in the ORGANISATION'S timezone: "07:00 on the 1st"
 * means 07:00 in Riyadh, not on the server's clock — a UTC server would
 * otherwise send the monthly report three hours early, into the wrong day.
 */
export const DISTRIBUTION_CADENCES = ['daily', 'fortnightly', 'monthly', 'quarterly', 'on_approval'];
/** The calendar date an instant falls on, in a timezone. */
export function localDateOf(instant, timezone) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(instant);
    const get = (type) => Number(parts.find((part) => part.type === type)?.value);
    return { year: get('year'), month: get('month'), day: get('day') };
}
/** How far a timezone is ahead of UTC at an instant, in minutes. */
function offsetMinutes(instant, timezone) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).formatToParts(instant);
    const get = (type) => Number(parts.find((part) => part.type === type)?.value);
    const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
    return Math.round((asUtc - instant.getTime()) / 60_000);
}
/** The instant a local date and time falls at, in a timezone. */
export function zonedInstant(date, time, timezone) {
    const [hour, minute] = time.split(':').map(Number);
    const guess = Date.UTC(date.year, date.month - 1, date.day, hour, minute);
    // Two passes settle a guess that straddles a change of offset.
    let instant = guess - offsetMinutes(new Date(guess), timezone) * 60_000;
    instant = guess - offsetMinutes(new Date(instant), timezone) * 60_000;
    return new Date(instant);
}
const addDays = (date, days) => {
    const moved = new Date(Date.UTC(date.year, date.month - 1, date.day + days));
    return { year: moved.getUTCFullYear(), month: moved.getUTCMonth() + 1, day: moved.getUTCDate() };
};
const addMonths = (date, months) => {
    const moved = new Date(Date.UTC(date.year, date.month - 1 + months, 1));
    return { year: moved.getUTCFullYear(), month: moved.getUTCMonth() + 1, day: date.day };
};
const iso = (date) => `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;
const lastDayOf = (year, month) => new Date(Date.UTC(year, month, 0)).getUTCDate();
/** The run days a cadence uses, starting from a month: [dates in that month]. */
function runDaysIn(cadence, year, month) {
    switch (cadence) {
        case 'fortnightly':
            return [
                { year, month, day: 1 },
                { year, month, day: 16 },
            ];
        case 'monthly':
            return [{ year, month, day: 1 }];
        case 'quarterly':
            return [1, 4, 7, 10].includes(month) ? [{ year, month, day: 1 }] : [];
        default:
            return [];
    }
}
/**
 * The next run strictly after `after`. Null for `on_approval`, which runs when
 * the thing it distributes is approved rather than on a clock.
 */
export function nextRunAt(cadence, runTimeLocal, timezone, after) {
    if (cadence === 'on_approval')
        return null;
    const today = localDateOf(after, timezone);
    if (cadence === 'daily') {
        for (let offset = 0; offset < 3; offset += 1) {
            const candidate = zonedInstant(addDays(today, offset), runTimeLocal, timezone);
            if (candidate > after)
                return candidate;
        }
        return null;
    }
    for (let monthOffset = 0; monthOffset < 15; monthOffset += 1) {
        const month = addMonths({ ...today, day: 1 }, monthOffset);
        for (const day of runDaysIn(cadence, month.year, month.month)) {
            const candidate = zonedInstant(day, runTimeLocal, timezone);
            if (candidate > after)
                return candidate;
        }
    }
    return null;
}
/**
 * The period a run reports on: the one that has just closed.
 *
 *   daily        yesterday
 *   fortnightly  the half-month just ended (1st–15th, or 16th–end)
 *   monthly      last month
 *   quarterly    last quarter
 */
export function reportWindow(cadence, runAt, timezone) {
    const today = localDateOf(runAt, timezone);
    switch (cadence) {
        case 'daily': {
            const yesterday = addDays(today, -1);
            return { from: iso(yesterday), to: iso(yesterday) };
        }
        case 'fortnightly': {
            if (today.day >= 16)
                return { from: iso({ ...today, day: 1 }), to: iso({ ...today, day: 15 }) };
            const previous = addMonths({ ...today, day: 1 }, -1);
            return { from: iso({ ...previous, day: 16 }), to: iso({ ...previous, day: lastDayOf(previous.year, previous.month) }) };
        }
        case 'monthly': {
            const previous = addMonths({ ...today, day: 1 }, -1);
            return { from: iso({ ...previous, day: 1 }), to: iso({ ...previous, day: lastDayOf(previous.year, previous.month) }) };
        }
        case 'quarterly': {
            const quarterStartMonth = Math.floor((today.month - 1) / 3) * 3 + 1;
            const start = addMonths({ year: today.year, month: quarterStartMonth, day: 1 }, -3);
            const end = addMonths({ year: today.year, month: quarterStartMonth, day: 1 }, -1);
            return { from: iso({ ...start, day: 1 }), to: iso({ ...end, day: lastDayOf(end.year, end.month) }) };
        }
        default:
            return null;
    }
}
/** A calendar date, "YYYY-MM-DD". */
export const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
function parseIsoDate(value) {
    const [year, month, day] = value.split('-').map(Number);
    return { year, month, day };
}
/**
 * The last moment of a calendar date in a timezone (DATA-13). An access that
 * "ends on 1 October" ends as 1 October ends where the organisation is — not at
 * midnight UTC at its start, which in Riyadh is 03:00 on the day itself.
 */
export function endOfLocalDay(date, timezone) {
    return new Date(zonedInstant(parseIsoDate(date), '23:59', timezone).getTime() + 59_999);
}
/** The first moment of a calendar date in a timezone. */
export function startOfLocalDay(date, timezone) {
    return zonedInstant(parseIsoDate(date), '00:00', timezone);
}
//# sourceMappingURL=distribution.js.map